import { useState, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { BackendFilter, SessionListResponse, SelectColumn, SessionListV5Params, FilterColumn, FilterOperator } from '../../../../interfaces/sessions';
import { SessionHistoryTabs } from '../../../../components/History/views';
import { getServicesManager } from '../../../../services';
import { BackendTables } from '../../../../interfaces/db';
import {
  CallMetricColumns,
  CallbackRequestColumns,
  MessagingDataColumns,
  PROSPECT_INFO_COLUMNS,
  ReviewStatusColumns,
  SESSION_METRICS_COLUMNS,
  SessionV2Columns,
  SessionAgentsColumns,
  SessionStatesColumns,
  TranscriptDataColumns
} from '../../../../interfaces/table';

export interface ExportProgress {
  current: number;
  total: number;
  isExporting: boolean;
  error: string | null;
}

export interface UseSessionExportProps {
  tab: SessionHistoryTabs;
  passed_filter?: BackendFilter;
}

const DEBOUNCE_MS = 2000; // 2 second delay between fetches
const MAX_SESSIONS_PER_FETCH = 200;

export function useSessionExport({ tab, passed_filter }: UseSessionExportProps) {
  const [progress, setProgress] = useState<ExportProgress>({
    current: 0,
    total: 0,
    isExporting: false,
    error: null,
  });

  const [exportedSessions, setExportedSessions] = useState<SessionListResponse[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const team = useSelector((state: RootState) => state.team);
  const user = useSelector((state: RootState) => state.user.value);
  const dispositionInfoOptions = useSelector((state: RootState) => state.dispositionOptions.value);

  // Helper to check if a disposition/sentiment/purpose column has options
  const hasDispositionOptions = (column: SESSION_METRICS_COLUMNS): boolean => {
    if (!dispositionInfoOptions) return false;
    const matching_filters = dispositionInfoOptions.filter((x) => x.field_name === column).map((v) => v.choices).flat();
    return matching_filters.length > 0;
  };

  // Define columns to fetch - all available columns including new ones
  const columns: SelectColumn[] = useMemo(() => {
    const baseColumns: SelectColumn[] = [
      // Basic session info (REQUIRED columns for converter)
      { column_name: SessionV2Columns.SESSION_ID, table: BackendTables.SESSIONS_V2 },
      { column_name: SessionV2Columns.STARTED_AT, table: BackendTables.SESSIONS_V2 },
      { column_name: SessionV2Columns.CONVERSATION_TYPE, table: BackendTables.SESSIONS_V2 }, // REQUIRED by converter
      { column_name: SessionV2Columns.AGENT_ADDRESS, table: BackendTables.SESSIONS_V2 },
      { column_name: SessionV2Columns.CUSTOMER_ADDRESS, table: BackendTables.SESSIONS_V2 },
      { column_name: SessionV2Columns.RESOURCE_ID, table: BackendTables.SESSIONS_V2 },

      // Call metrics
      { column_name: CallMetricColumns.CALL_DURATION, table: BackendTables.CALL_METRICS },
      { column_name: CallMetricColumns.PARALLEL_LINES, table: BackendTables.CALL_METRICS },

      // Prospect info
      { column_name: PROSPECT_INFO_COLUMNS.PROSPECT_NAME, table: BackendTables.PROSPECT_INFO },
      { column_name: PROSPECT_INFO_COLUMNS.PROSPECT_TITLE, table: BackendTables.PROSPECT_INFO },
      { column_name: PROSPECT_INFO_COLUMNS.COMPANY_NAME, table: BackendTables.PROSPECT_INFO },
      { column_name: PROSPECT_INFO_COLUMNS.CADENCE, table: BackendTables.PROSPECT_INFO },
      { column_name: PROSPECT_INFO_COLUMNS.PROSPECT_LIST, table: BackendTables.PROSPECT_INFO },
      { column_name: PROSPECT_INFO_COLUMNS.PLATFORM_PERSON_URL, table: BackendTables.PROSPECT_INFO },

      // Transcript data (rep and prospect text with timestamps)
      { column_name: TranscriptDataColumns.REP_TEXT, table: BackendTables.TRANSCRIPT_DATA },
      { column_name: TranscriptDataColumns.PROSPECT_TEXT, table: BackendTables.TRANSCRIPT_DATA },
      { column_name: TranscriptDataColumns.REP_STARTS_STR, table: BackendTables.TRANSCRIPT_DATA },
      { column_name: TranscriptDataColumns.PROSPECT_STARTS_STR, table: BackendTables.TRANSCRIPT_DATA }
    ];

    // Conditionally add disposition/sentiment/purpose if they have options
    if (hasDispositionOptions(SESSION_METRICS_COLUMNS.USER_DISPOSITION)) {
      baseColumns.push({ column_name: SESSION_METRICS_COLUMNS.USER_DISPOSITION as any, table: BackendTables.SESSION_METRICS });
    }
    if (hasDispositionOptions(SESSION_METRICS_COLUMNS.USER_SENTIMENT)) {
      baseColumns.push({ column_name: SESSION_METRICS_COLUMNS.USER_SENTIMENT as any, table: BackendTables.SESSION_METRICS });
    }
    if (hasDispositionOptions(SESSION_METRICS_COLUMNS.USER_PURPOSE)) {
      baseColumns.push({ column_name: SESSION_METRICS_COLUMNS.USER_PURPOSE as any, table: BackendTables.SESSION_METRICS });
    }

    return baseColumns;
  }, [dispositionInfoOptions]);

  const exportSessions = useCallback(
    async (startDate: Date, endDate: Date) => {
      console.log('🔄 [Export] Starting export process', { startDate, endDate, tab });

      // Validate date range is within 1 week
      const weekInMs = 7 * 24 * 60 * 60 * 1000;
      if (endDate.getTime() - startDate.getTime() > weekInMs) {
        console.error('❌ [Export] Date range exceeds 1 week');
        setProgress({
          current: 0,
          total: 0,
          isExporting: false,
          error: 'Date range cannot exceed 1 week',
        });
        return [];
      }

      // Build filter based on tab (matching session-fetch-hook logic)
      let filter = passed_filter || [];

      // Add conversation_type filter based on tab
      if (tab === SessionHistoryTabs.CALLS || tab === SessionHistoryTabs.MISSED) {
        const conversationTypeFilter: FilterColumn = {
          column_name: SessionV2Columns.CONVERSATION_TYPE,
          table: BackendTables.SESSIONS_V2,
          operator: FilterOperator.EQ,
          value_safe: 'CALL'
        };
        // Add to filter if not already present
        const hasConversationFilter = filter.some(andGroup =>
          andGroup.some(f => f.column_name === SessionV2Columns.CONVERSATION_TYPE)
        );
        if (!hasConversationFilter) {
          filter = [[conversationTypeFilter], ...filter];
        }
      } else if (tab === SessionHistoryTabs.TEXTS || tab === SessionHistoryTabs.CHAT) {
        const conversationTypeFilter: FilterColumn = {
          column_name: SessionV2Columns.CONVERSATION_TYPE,
          table: BackendTables.SESSIONS_V2,
          operator: FilterOperator.EQ,
          value_safe: tab === SessionHistoryTabs.CHAT ? 'CHAT' : 'TEXT'
        };
        const hasConversationFilter = filter.some(andGroup =>
          andGroup.some(f => f.column_name === SessionV2Columns.CONVERSATION_TYPE)
        );
        if (!hasConversationFilter) {
          filter = [[conversationTypeFilter], ...filter];
        }
      }

      // Create abort controller
      abortControllerRef.current = new AbortController();

      setProgress({
        current: 0,
        total: 0,
        isExporting: true,
        error: null,
      });
      setExportedSessions([]);

      const allSessions: SessionListResponse[] = [];
      let currentEnd = endDate;
      let currentStart = startDate;
      let fetchCount = 0;

      try {
        // Keep fetching until we get no more results
        while (true) {
          if (abortControllerRef.current?.signal.aborted) {
            console.warn('⚠️ [Export] Export cancelled by user');
            throw new Error('Export cancelled');
          }

          fetchCount++;

          // Build params for API call (matching how session-fetch-hook does it)
          // Note: start should be EARLIER (further in past), end should be LATER (more recent)
          const params: SessionListV5Params = {
            cnf: filter,
            select: columns,
            start: currentStart,
            end: currentEnd
          };

          // Fetch sessions
          const result = await getServicesManager().getListUserSessionsV5(params);

          // If no results, we're done
          if (!result || result.length === 0) {
            break;
          }

          // Add results to our collection
          allSessions.push(...result);
          setExportedSessions((prev) => [...prev, ...result]);
          console.log(`📈 [Export] Total sessions collected: ${allSessions.length}`);

          // Update progress (show number of fetches and total sessions)
          setProgress((prev) => ({
            ...prev,
            current: fetchCount,
            total: fetchCount + 1, // We don't know total, so show current + 1
          }));

          // Get the last item's timestamp to use as the new END for next fetch
          // The last item has the OLDEST timestamp in this batch (since results are sorted newest first)
          const lastItem = result[result.length - 1];
          const lastItemDate = lastItem.result.get(SessionV2Columns.STARTED_AT) as Date;

          if (!lastItemDate) {
            console.warn(`⚠️ [Export] Last item has no started_at, stopping pagination`);
            break;
          }

          // Check if we've reached the start date boundary
          if (lastItemDate.getTime() <= startDate.getTime()) {
            console.log(`🏁 [Export] Reached start date boundary, stopping pagination`);
            break;
          }

          // Use the last item's timestamp as the new end (paginate backwards in time)
          currentEnd = lastItemDate;

          // Debounce between fetches
          await new Promise((resolve) => setTimeout(resolve, DEBOUNCE_MS));
        }

        setProgress({
          current: allSessions.length,
          total: allSessions.length,
          isExporting: false,
          error: null,
        });

        return allSessions;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Export failed';
        setProgress({
          current: 0,
          total: 0,
          isExporting: false,
          error: errorMessage,
        });
        return [];
      }
    },
    [tab, passed_filter, user, columns]
  );

  const cancelExport = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setProgress({
        current: 0,
        total: 0,
        isExporting: false,
        error: 'Export cancelled',
      });
    }
  }, []);

  const resetExport = useCallback(() => {
    setProgress({
      current: 0,
      total: 0,
      isExporting: false,
      error: null,
    });
    setExportedSessions([]);
  }, []);

  return {
    exportSessions,
    cancelExport,
    resetExport,
    progress,
    exportedSessions,
    columns, // Return columns so CSV builder knows what was fetched
  };
}
